/**
 * Shared UI utilities for the Technology > FinOps module.
 *
 * Deliberately self-contained: the formatters/StatCard/LoaderBlock are copied
 * from `components/observability/shared.tsx` instead of cross-imported so the
 * two modules (different RBAC domains) stay independent.
 */

import { addDaysISO } from '@/../../shared/dates';
import { Card } from '@/components/ui/card';
import { WinclapLoader } from '@/components/ui/winclap-loader';

export const fmtUSD = (n: number | null | undefined) =>
  n == null ? '—' : `$${Math.round(n).toLocaleString('en-US')}`;

export const fmtUSDCents = (n: number | null | undefined) =>
  n == null ? '—' : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const fmtNum = (n: number | null | undefined) =>
  n == null ? '—' : Math.round(n).toLocaleString('en-US');

export const fmtPct = (n: number | null | undefined) =>
  n == null ? '—' : `${n.toFixed(1)}%`;

export const fmtMonth = (m: string) => {
  const d = m.length >= 7 ? m.substring(0, 7) : m;
  const [y, mo] = d.split('-');
  return new Date(+y, +mo - 1).toLocaleString('en-US', { month: 'short', year: '2-digit' });
};

/**
 * Date helpers for the cloud tabs. Periods travel as plain ISO strings
 * (`YYYY-MM-DD`); everything is computed in UTC so a browser east or west of
 * the collector never shifts a week by a day.
 */
export { addDaysISO };

/** '2026-08-03' → 'Aug 3'. */
export const fmtDay = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleString('en-US', {
    month: 'short', day: 'numeric', timeZone: 'UTC',
  });
};

/** Monday → 'Aug 3 – Aug 9'. */
export const fmtWeekRange = (monday: string) => `${fmtDay(monday)} – ${fmtDay(addDaysISO(monday, 6))}`;

export const daysInMonth = (d: Date) =>
  new Date(d.getUTCFullYear(), d.getUTCMonth() + 1, 0).getDate();

/** Fractional change, null when there is no comparable base. */
export const deltaOf = (current: number, prior: number) =>
  prior > 0 ? (current - prior) / prior : null;

/** MoM/WoW deltas arrive as fractions (0.124 → +12.4%). */
export const fmtDelta = (d: number | null | undefined) =>
  d == null ? '—' : `${d >= 0 ? '+' : ''}${(d * 100).toFixed(1)}%`;


export function LoaderBlock() {
  return (
    <div className="flex items-center justify-center py-20">
      <WinclapLoader />
    </div>
  );
}

/** Cost going up is bad — rose for increases, emerald for savings. */
export function DeltaBadge({ value }: { value: number | null | undefined }) {
  if (value == null) return <span className="text-muted-foreground">—</span>;
  return (
    <span className={value >= 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}>
      {fmtDelta(value)}
    </span>
  );
}

/**
 * Informative empty card. Cloud data accumulates forward only (GCP export was
 * enabled recently; AWS snapshots exist only after the collector runs), so an
 * explanation beats rendering zeros.
 */
export function EmptyState({ title, description }: { title: string; description?: string }) {
  return (
    <Card className="p-10 text-center">
      <p className="text-sm font-medium">{title}</p>
      {description && <p className="text-xs text-muted-foreground mt-1.5 max-w-md mx-auto">{description}</p>}
    </Card>
  );
}

/** La card de KPI es compartida (components/ui/stat-card); queda re-exportada por los imports viejos. */
export { StatCard } from '@/components/ui/stat-card';
